import type {
  MenuCollapse,
  MenuLink,
  MenuSection,
  OnlyWhenProps,
  SidebarRoutesContextProps,
} from './types';

export const getHrefPaths = (
  child: MenuLink | MenuCollapse,
  onlyWhenConditions: SidebarRoutesContextProps['onlyWhen'],
) => {
  if (typeof child.href === 'string') return child.href;
  return child.href({
    ...onlyWhenConditions,
  });
};

export const getMatchPaths = (
  child: MenuLink,
  _onlyWhenConditions: SidebarRoutesContextProps['onlyWhen'],
) => {
  if (!child.matchPaths) return [];
  return child.matchPaths;
};

/**
 * Recursively filter menu items based on onlyWhen condition
 */
const filterMenuItem = (
  item: MenuLink | MenuCollapse,
  onlyWhenConditions: OnlyWhenProps,
): MenuLink | MenuCollapse | null => {
  // Check if this item passes the onlyWhen condition
  const shouldShow = item.onlyWhen
    ? item.onlyWhen(onlyWhenConditions) === true
    : true;

  if (!shouldShow) return null;

  // If it's a collapse menu with children, filter its children recursively
  if (item.type === 'collapse') {
    const filteredChildren = item.children
      .map((child) => filterMenuItem(child, onlyWhenConditions))
      .filter((child): child is MenuLink => child !== null);

    // Only return this collapse menu if it has visible children
    if (filteredChildren.length === 0) return null;

    // Create a new object to avoid mutating the original
    return {
      ...item,
      children: filteredChildren,
    };
  }

  // For simple links, just return the item
  return item;
};

/**
 * @description only show menu that have onlyWhenCondition truthy, including nested children
 */
export const getFilteredMenu = (
  menus: MenuSection[],
  onlyWhenConditions: OnlyWhenProps,
) => {
  return (
    menus
      .filter((section) => {
        const ableToShowRootLink = section.onlyWhen
          ? section.onlyWhen(onlyWhenConditions) === true
          : true;

        return ableToShowRootLink;
      })
      .map((section) => {
        // Filter children recursively
        const filteredChildren = section.children
          .map((child) => filterMenuItem(child, onlyWhenConditions))
          .filter((child): child is MenuLink | MenuCollapse => child !== null);

        // Create a new section to avoid mutating the original
        return {
          ...section,
          children: filteredChildren,
        };
      })
      /**
       * do not show section if there's no children
       */
      .filter((val) => val.children.length > 0)
  );
};

/**
 * @description remove all query params and hash
 */
export const pathnameOnly = (pathname: string) => {
  let cleaned = pathname;
  cleaned = cleaned.split('?')[0] ?? pathname;
  cleaned = cleaned.split('#')[0] ?? pathname;
  return cleaned;
};

/**
 * @description Getting list of string or regex to determine active state
 */
export const getMenuMatchesOrPaths = (
  menu: MenuLink | MenuCollapse,
  onlyWhenConditions: OnlyWhenProps,
  options?: {
    menuHierarchyLevel?: 'root' | 'child';
  },
) => {
  let regexPaths: RegExp[] = [];
  let hrefPaths: string[] = [];
  const menuHierarchyLevel = options?.menuHierarchyLevel ?? 'child';

  if (menu.type === 'collapse') {
    regexPaths = menu.children.flatMap((child) =>
      getMatchPaths(child, onlyWhenConditions),
    );
    hrefPaths = menu.children.map((child) =>
      getHrefPaths(child, onlyWhenConditions),
    );
  }

  if (menu.type === 'link') {
    const matches = getMatchPaths(menu, onlyWhenConditions);
    regexPaths = [...regexPaths, ...matches];
    hrefPaths = [...hrefPaths, getHrefPaths(menu, onlyWhenConditions)];
  }
  /**
   * to keep root link open if any the child link is active
   */
  if (menuHierarchyLevel === 'root') {
    return [...regexPaths, ...hrefPaths];
  }

  if (regexPaths.length > 0) return regexPaths;
  return hrefPaths;
};

export const isMenuActive = (
  paths: ReturnType<typeof getMenuMatchesOrPaths>,
  currentPathname: SidebarRoutesContextProps['currentPathname'],
  options?: {
    exactMatch?: boolean;
  },
) => {
  return paths.some((path) => {
    if (path instanceof RegExp) {
      return currentPathname.match(path);
    }

    if (options?.exactMatch) {
      return currentPathname === path;
    }

    return currentPathname === path || currentPathname.startsWith(path);
  });
};

/**
 * Recursively finds the parent menu id for a given path.
 * Returns the id of the first menu (section/collapse/link) whose descendant (or itself) matches the current path.
 */
export function findMenuIdByPath(
  menus: (MenuSection | MenuCollapse | MenuLink)[],
  currentPath: string,
): string | null {
  for (const menu of menus) {
    // 1. If this is a section, check its children recursively
    if (menu.type === 'section' && menu.children) {
      const childMatch = findMenuIdByPath(menu.children, currentPath);
      if (childMatch) return childMatch;
    }

    // 2. If this is a collapse, check its children recursively, or itself
    else if (menu.type === 'collapse' && menu.children) {
      const childMatch = findMenuIdByPath(menu.children, currentPath);
      if (childMatch) return childMatch;
      if (menu.href === currentPath) return menu.id;
    }

    // 3. If this is a link, check href
    else if (menu.type === 'link') {
      if (menu.href === currentPath) return menu.id;
      // check matchPaths if they exist
      if (
        menu.matchPaths &&
        menu.matchPaths.some((path) => path.test(currentPath))
      ) {
        return menu.id;
      }
    }
  }
  // No match found in this branch
  return null;
}
